import { useState } from "react";
import { Bell, Check, CheckCircle2, AlertCircle, AlertTriangle, Info } from "lucide-react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { useAlerts } from "@/hooks/useAlerts";
import { useNavigate } from "react-router-dom";
import { getAlertAction } from "@/lib/alertActions";

const severityIcon: Record<string, React.ElementType> = {
  critical: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

const severityColor: Record<string, string> = {
  critical: "text-destructive",
  warning: "text-warning",
  info: "text-info",
};

export function NotificationBell() {
  const { alerts, unreadCount, markRead, markAllRead } = useAlerts();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const recent = alerts.slice(0, 8);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button data-tour="notification-bell" variant="ghost" size="icon" className="relative">
          <Bell className="h-5 w-5 text-muted-foreground" />
          {unreadCount > 0 && (
            <span className="absolute top-1 right-1 flex h-4 w-4 items-center justify-center rounded-full bg-brand text-[10px] font-bold text-brand-foreground">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <p className="text-sm font-semibold text-foreground">Notifications</p>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs text-brand" onClick={() => markAllRead.mutate()}>
              <Check className="h-3 w-3 mr-1" /> Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-80">
          {recent.length === 0 ? (
            <div className="py-8 text-center text-sm text-muted-foreground">No notifications</div>
          ) : (
            <div className="divide-y">
              {recent.map((alert) => {
                const Icon = severityIcon[alert.severity || "info"] || Info;
                const action = getAlertAction(alert);
                return (
                  <button
                    key={alert.id}
                    className={cn(
                      "flex items-start gap-3 w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors",
                      !alert.is_read && "bg-brand/5"
                    )}
                    onClick={() => {
                      if (!alert.is_read) markRead.mutate(alert.id);
                      if (action) {
                        setOpen(false);
                        navigate(action.path);
                      }
                    }}
                  >
                    <Icon className={cn("h-4 w-4 mt-0.5 shrink-0", severityColor[alert.severity || "info"])} />
                    <div className="flex-1 min-w-0">
                      <p className={cn("text-xs truncate", !alert.is_read ? "font-semibold text-foreground" : "text-muted-foreground")}>
                        {alert.title}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{alert.message}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <p className="text-[10px] text-muted-foreground">{format(new Date(alert.created_at), "MMM d, h:mm a")}</p>
                        {action && <span className="text-[10px] font-medium text-brand">{action.label} →</span>}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
        <div className="border-t p-2">
          <Button variant="ghost" size="sm" className="w-full text-xs text-brand" onClick={() => { setOpen(false); navigate("/alerts"); }}>
            View all alerts
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
